<template>
  <div>
    <div class="wz-page-header">
      <h1 class="wz-page-title">Nueva solicitud de vacaciones</h1>
    </div>

    <wz-card title="Datos de la solicitud">

      <!-- Paso 1: Empresa y colaborador -->
      <div class="flex flex-wrap -mx-2">
        <div class="w-full md:w-1/2 mb-3 px-2">
          <wz-select
            label="Empresa"
            required
            :items="companies"
            :value="form.companyId"
            defaultOption="Seleccione una empresa"
            :error="$v.form.companyId.$error && !$v.form.companyId.required ? 'Empresa es requerida' : ''"
            @change="onCompanyChange"
          />
        </div>
        <div class="w-full md:w-1/2 mb-3 px-2">
          <wz-select-search
            label="Colaborador"
            required
            :items="employees"
            :value="form.employeeId"
            :disabled="!form.companyId"
            defaultOption="Buscar por nombre o número de documento"
            :error="$v.form.employeeId.$error && !$v.form.employeeId.required ? 'Colaborador es requerido' : ''"
            @change="onEmployeeChange"
          />
        </div>
      </div>

      <!-- Alerta sin contrato -->
      <wz-alert v-if="noActiveContract" variant="danger" icon="ri-error-warning-line" class="mb-3">
        Este colaborador no tiene contrato vigente.
      </wz-alert>

      <wz-alert v-if="complianceError" variant="danger" icon="ri-lock-line" class="mb-3">
        <strong>Documentación incompleta.</strong> No se puede crear la solicitud.
        Documentos faltantes: {{ complianceError.join(', ') }}
      </wz-alert>

      <!-- Resumen del empleado -->
      <div v-if="balance && !noActiveContract" class="mb-4">
        <div class="flex flex-wrap -mx-2">
          <div class="w-full md:w-1/4 mb-2 px-2">
            <div class="stat-card p-3 border rounded text-center">
              <div class="stat-label text-muted text-sm">Empresa</div>
              <div class="stat-value font-bold">{{ balance.companyName }}</div>
            </div>
          </div>
          <div class="w-full md:w-1/4 mb-2 px-2">
            <div class="stat-card p-3 border rounded text-center">
              <div class="stat-label text-muted text-sm">Régimen laboral</div>
              <div class="stat-value font-bold">{{ balance.laborRegime }}</div>
            </div>
          </div>
          <div class="w-full md:w-1/4 mb-2 px-2">
            <div class="stat-card p-3 border rounded text-center">
              <div class="stat-label text-muted text-sm">Días pendientes</div>
              <div class="stat-value font-bold text-primary">{{ balance.totalDaysPending }}</div>
            </div>
          </div>
          <div class="w-full md:w-1/4 mb-2 px-2" v-if="balance.truncDays > 0">
            <div class="stat-card p-3 border rounded text-center">
              <div class="stat-label text-muted text-sm">Días truncos</div>
              <div class="stat-value font-bold text-secondary">{{ balance.truncDays }}</div>
            </div>
          </div>
        </div>

        <wz-alert v-if="balance.hasExpiringPeriods" variant="warning" icon="ri-alarm-warning-line" class="mt-2">
          Hay períodos por vencer próximamente.
        </wz-alert>

        <wz-alert v-if="isAdvanceVacation" variant="warning" icon="ri-information-line" class="mt-2">
          El colaborador no ha completado 1 año de servicios. Este registro se considerará adelanto de vacaciones (D.L. 1405).
        </wz-alert>
      </div>

      <!-- Tipo de registro: descanso físico o venta (D.L. 713 Art. 19) -->
      <div v-if="balance && !noActiveContract" class="mb-4">
        <wz-select
          label="Tipo"
          :items="saleTypeItems"
          :value="saleType"
          @change="onSaleTypeChange"
        />
      </div>

      <!-- Paso 2: Seleccionar período (para venta es obligatorio; para físico opcional = FIFO) -->
      <div v-if="balance && !noActiveContract" class="mb-4">
        <wz-select
          :label="isSale ? 'Período vacacional a vender' : 'Período vacacional (opcional)'"
          :items="periodSelectItems"
          :value="form.vacationPeriodId"
          :defaultOption="isSale ? 'Seleccione el período' : 'Automático (más antiguo con saldo)'"
          @change="onPeriodChange"
        />
        <p v-if="!isSale" class="text-muted text-sm mt-1">
          Si no elige un período, el sistema tomará automáticamente el más antiguo con días disponibles, para evitar que caduquen.
        </p>
        <wz-alert v-if="periodOptions.length === 0" variant="info" class="mt-2">
          Este colaborador no tiene períodos con días disponibles en esta empresa.
        </wz-alert>
        <wz-alert v-if="saleNoCompletePeriods" variant="warning" icon="ri-alert-line" class="mt-2">
          El colaborador no tiene períodos vacacionales completos para vender.
        </wz-alert>
      </div>

      <!-- Venta de vacaciones -->
      <div v-if="isSale && balance && !noActiveContract" class="mb-4">
        <div class="flex flex-wrap -mx-2">
          <div class="w-full md:w-1/3 mb-3 px-2">
            <wz-input
              label="Días a vender"
              type="number"
              min="1"
              :max="saleCap"
              v-model.number="sale.soldDays"
            />
            <p class="text-muted text-sm mt-1">Tope según régimen ({{ balance.laborRegime || 'GENERAL' }}): {{ saleCap }} días.</p>
          </div>
          <div class="w-full md:w-1/3 mb-3 px-2">
            <wz-input label="Monto a pagar (S/)" :value="saleAmountDisplay" disabled />
          </div>
          <div class="w-full md:w-1/3 mb-3 px-2">
            <label class="wz-form-label">Acuerdo escrito <span class="text-danger">*</span></label>
            <input type="file" class="wz-form-control" accept=".pdf,.jpg,.jpeg,.png" @change="onAgreementFile" />
          </div>
        </div>
        <wz-alert v-if="saleMinPhysicalWarning" variant="danger" icon="ri-error-warning-line" class="mt-1">
          {{ saleMinPhysicalWarning }}
        </wz-alert>
        <wz-alert variant="info" icon="ri-information-line" class="mt-1">
          La compensación se paga como adicional remunerativo en la planilla; se conservan mínimo {{ saleMinPhysical }} días de descanso físico.
        </wz-alert>
      </div>

      <!-- Paso 3: Fechas (solo descanso físico) -->
      <div v-if="!isSale && balance && !noActiveContract" class="flex flex-wrap mb-3 -mx-2">
        <div class="w-full md:w-1/3 mb-3 px-2">
          <wz-datepicker
            label="Fecha de inicio"
            required
            v-model="form.startDate"
            :min-date="minStartDate"
            :error="$v.form.startDate.$error && !$v.form.startDate.required ? 'La fecha de inicio es obligatoria' : ''"
            @change="calculateDays"
          />
          <p v-if="minAdvanceDays > 0" class="text-muted text-sm mt-1">
            Puede registrar vacaciones desde el <strong>{{ minStartDateDisplay }}</strong> (hasta {{ minAdvanceDays }} día(s) atrás)
          </p>
        </div>
        <div class="w-full md:w-1/3 mb-3 px-2">
          <wz-datepicker
            label="Fecha de fin"
            required
            v-model="form.endDate"
            :error="$v.form.endDate.$error && !$v.form.endDate.required ? 'La fecha de fin es obligatoria' : ''"
            @change="calculateDays"
          />
        </div>
        <div class="w-full md:w-1/3 flex items-center px-2">
          <div v-if="calendarDays !== null" class="md:mt-3">
            <wz-badge :variant="daysExceedBalance ? 'danger-solid' : 'success-solid'">
              {{ calendarDays }} días calendario
            </wz-badge>
            <div v-if="daysExceedBalance" class="text-danger text-sm mt-1">
              Excede los días disponibles del período ({{ selectedPeriod ? selectedPeriod.daysPending : 0 }})
            </div>
          </div>
        </div>
      </div>

      <!-- Alerta preventiva: solape con DM / licencia (no bloquea; la validación dura vive en el backend) -->
      <wz-alert
        v-for="(o, i) in overlaps"
        :key="'overlap-' + i"
        variant="warning"
        icon="ri-information-line"
        class="mb-3"
      >
        {{ overlapLabel(o) }}
      </wz-alert>

      <!-- Observaciones -->
      <div v-if="balance && !noActiveContract" class="flex flex-wrap mb-3 -mx-2">
        <div class="w-full px-2">
          <wz-textarea
            label="Observaciones"
            v-model="form.observations"
            placeholder="Observaciones opcionales"
            :rows="3"
          />
        </div>
      </div>

      <!-- Botones -->
      <div class="flex justify-end gap-2 mt-2">
        <wz-button variant="light" @click="$router.push({ name: 'vacation-requests-list' })">
          Cancelar
        </wz-button>
        <wz-button
          variant="primary"
          class="ml-2"
          :disabled="isSale ? !canSell : !canSave"
          @click="save()"
        >
          {{ isSale ? 'Registrar venta' : 'Guardar' }}
        </wz-button>
      </div>

    </wz-card>
  </div>
</template>

<script>
import { Alert } from "@wizepe/wizeui"
import { required } from 'vuelidate/lib/validators'
import { VacationService, EmployeeService, CompanyService } from '@/core/services'
import * as moment from 'moment'

export default {
  name: 'VacationRequestCreate',
  data() {
    return {
      companies: [],
      employees: [],
      balance: null,
      noActiveContract: false,
      complianceError: null,
      calendarDays: null,
      overlaps: [],
      minAdvanceDays: 3,
      minStartDate: null,
      periodOptions: [], // períodos seleccionables (VIGENTES + VENCIDOS con saldo) del colaborador+empresa
      preselectedPeriodId: null, // período a preseleccionar desde ?vacationPeriodId (one-shot)
      saleType: 'FISICO', // FISICO (descanso) | VENTA (compensación económica)
      saleTypeItems: [
        { key: 'FISICO', label: 'Descanso físico' },
        { key: 'VENTA',  label: 'Venta (compensación económica)' },
      ],
      sale: { soldDays: null, file: null },
      form: {
        companyId: null,
        employeeId: null,
        contractId: null,
        vacationPeriodId: null,
        startDate: null,
        endDate: null,
        observations: '',
      },
    }
  },
  validations: {
    form: {
      companyId:        { required },
      employeeId:       { required },
      startDate:        { required },
      endDate:          { required },
    },
  },
  computed: {
    isSale() { return this.saleType === 'VENTA' },
    // Tope y mínimo físico por régimen (MYPE micro/pequeña = 7/8; resto = 15/15).
    saleCap() {
      const r = this.balance ? this.balance.laborRegime : null
      return (r === 'MYPE_MICRO' || r === 'MYPE_PEQUENA') ? 7 : 15
    },
    saleMinPhysical() {
      const r = this.balance ? this.balance.laborRegime : null
      return (r === 'MYPE_MICRO' || r === 'MYPE_PEQUENA') ? 8 : 15
    },
    saleAmount() {
      const base = this.balance && this.balance.baseSalary ? Number(this.balance.baseSalary) : 0
      const d = Number(this.sale.soldDays) || 0
      return d > 0 ? (base / 30) * d : 0
    },
    saleAmountDisplay() {
      return this.saleAmount > 0 ? this.saleAmount.toFixed(2) : '—'
    },
    // Aviso de mínimo físico (usa el período seleccionado: earned - taken - sold >= minFísico).
    saleMinPhysicalWarning() {
      if (!this.isSale || !this.selectedPeriod || !this.sale.soldDays) return ''
      const p = this.selectedPeriod
      const earned = p.daysEarned != null ? p.daysEarned : (p.daysPending + (p.daysTaken || 0))
      const remaining = earned - (p.daysTaken || 0) - Number(this.sale.soldDays)
      if (remaining < this.saleMinPhysical) {
        return `Debe conservar al menos ${this.saleMinPhysical} días de descanso físico según su régimen.`
      }
      return ''
    },
    canSell() {
      return !!(this.form.contractId && this.form.vacationPeriodId && !this.complianceError
        && this.sale.soldDays > 0 && this.sale.soldDays <= this.saleCap
        && this.sale.file && !this.saleMinPhysicalWarning)
    },
    // En modo venta solo se pueden vender períodos COMPLETOS (año ganado, no trunco).
    saleablePeriods() {
      return this.periodOptions.filter(p => p.isComplete)
    },
    saleNoCompletePeriods() {
      return this.isSale && this.periodOptions.length > 0 && this.saleablePeriods.length === 0
    },
    // Opciones del dropdown de período: "YYYY-YYYY (N días disponibles) - VIGENTE/VENCIDO".
    periodSelectItems() {
      const list = this.isSale ? this.saleablePeriods : this.periodOptions
      return list.map(p => ({
        key: p.id,
        label: `${p.label} (${p.daysPending} días disponibles) - ${p.expired ? 'VENCIDO' : 'VIGENTE'}`,
      }))
    },
    // Informativo: menos de 1 año de servicios desde el inicio del contrato → adelanto (D.L. 1405)
    isAdvanceVacation() {
      if (!this.balance || !this.balance.contractStartDate) return false
      return moment().diff(moment(this.balance.contractStartDate), 'days') < 365
    },
    minStartDateDisplay() {
      return this.minStartDate ? moment(this.minStartDate).format('DD/MM/YYYY') : ''
    },
    selectedPeriod() {
      if (!this.form.vacationPeriodId) return null
      return this.periodOptions.find(p => p.id === this.form.vacationPeriodId) || null
    },
    daysExceedBalance() {
      if (!this.selectedPeriod || this.calendarDays === null) return false
      return this.calendarDays > this.selectedPeriod.daysPending
    },
    canSave() {
      return (
        this.form.companyId &&
        this.form.employeeId &&
        this.form.contractId &&
        !this.complianceError &&
        this.form.startDate &&
        this.form.endDate &&
        this.calendarDays > 0 &&
        !this.daysExceedBalance
      )
    },
  },
  methods: {
    async onCompanyChange(companyId) {
      this.form.companyId = companyId
      this.employees = []
      this.resetEmployeeSelection()
      if (!companyId) return
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await EmployeeService.listByCompany(companyId)
        const seen = new Set()
        this.employees = (resp.payload || [])
          .filter(e => e && e.id != null && !seen.has(e.id) && seen.add(e.id))
          .map(e => ({ key: e.id, label: e.description ?? '' }))
      } catch (e) {
        Alert.error('Error al cargar colaboradores de la empresa')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    resetEmployeeSelection() {
      this.form.employeeId = null
      this.form.contractId = null
      this.balance = null
      this.noActiveContract = false
      this.complianceError = null
      this.form.vacationPeriodId = null
      this.periodOptions = []
      this.form.startDate = null
      this.form.endDate = null
      this.calendarDays = null
      this.overlaps = []
    },
    async onEmployeeChange(employeeId) {
      this.resetEmployeeSelection()
      this.form.employeeId = employeeId
      if (!employeeId) return
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await VacationService.getBalanceByEmployeeCompany(employeeId, this.form.companyId)
        this.balance = resp.payload
        if (!this.balance || !this.balance.contractId) {
          this.noActiveContract = true
          this.balance = null
        } else {
          this.form.contractId = this.balance.contractId
          await this.fetchAvailablePeriods()
          // Preselección de período desde el link "Programar vacaciones" (one-shot)
          if (this.preselectedPeriodId) {
            const match = this.periodOptions.find(p => p.id === this.preselectedPeriodId)
            if (match) this.form.vacationPeriodId = match.id
            this.preselectedPeriodId = null
          }
        }
        try {
          const compResp = await EmployeeService.getCompliance(employeeId)
          const p = compResp.payload
          this.complianceError = p.compliant ? null : p.missingDocuments
        } catch (e) { /* non-critical */ }
      } catch (e) {
        this.noActiveContract = true
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async fetchAvailablePeriods() {
      this.periodOptions = []
      if (!this.form.employeeId || !this.form.companyId) return
      try {
        const resp = await VacationService.getAvailablePeriods(this.form.employeeId, this.form.companyId)
        this.periodOptions = resp.payload || []
      } catch (e) { /* si falla, queda vacío → el backend asigna por FIFO al guardar */ }
    },
    onPeriodChange(periodId) {
      // Vacío ('' / null) → automático (FIFO). Con valor → asignación explícita.
      this.form.vacationPeriodId = periodId || null
    },
    onSaleTypeChange(val) {
      this.saleType = val
      // Al cambiar de tipo, limpiar los campos del otro flujo.
      this.sale = { soldDays: null, file: null }
      this.form.startDate = null
      this.form.endDate = null
      this.calendarDays = null
      this.overlaps = []
    },
    onAgreementFile(e) {
      const f = e.target.files && e.target.files[0]
      this.sale.file = f || null
    },
    async sellVacation() {
      try {
        this.$store.dispatch('app/loading', true)
        const fd = new FormData()
        fd.append('soldDays', String(this.sale.soldDays))
        fd.append('agreementFile', this.sale.file)
        const resp = await VacationService.sellVacationDays(this.form.vacationPeriodId, fd)
        const r = resp.payload || {}
        Alert.success(`Venta registrada: S/ ${Number(r.amount || 0).toFixed(2)}`
          + (r.targetPeriod ? ` (compensación en planilla ${r.targetPeriod})` : ''))
        if (r.warning) Alert.alert('Advertencia', r.warning)
        this.$router.push({ name: 'vacation-requests-list' })
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al registrar la venta')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    calculateDays() {
      if (!this.form.startDate || !this.form.endDate) {
        this.calendarDays = null
        this.overlaps = []
        return
      }
      const start = new Date(this.form.startDate)
      const end   = new Date(this.form.endDate)
      const diff  = Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1
      this.calendarDays = diff > 0 ? diff : null
      this.checkOverlaps()
    },
    async checkOverlaps() {
      this.overlaps = []
      if (!this.form.employeeId || !this.form.startDate || !this.form.endDate) return
      try {
        const resp = await VacationService.checkOverlap(
          this.form.employeeId, this.form.startDate, this.form.endDate
        )
        this.overlaps = resp.payload || []
      } catch (e) { /* preventivo: si falla, no se muestra alerta ni se bloquea */ }
    },
    overlapLabel(o) {
      const kind = o.type === 'DESCANSO_MEDICO' ? 'un descanso médico' : 'una licencia'
      return `Las fechas seleccionadas se solapan con ${kind} del `
        + `${moment(o.startDate).format('DD/MM/YYYY')} al ${moment(o.endDate).format('DD/MM/YYYY')}.`
    },
    async save() {
      if (this.isSale) {
        if (!this.canSell) return
        return this.sellVacation()
      }
      this.$v.$touch()
      if (this.$v.$error || !this.canSave) return
      try {
        this.$store.dispatch('app/loading', true)
        await VacationService.createRequest({
          contractId:       this.form.contractId,
          vacationPeriodId: this.form.vacationPeriodId,
          startDate:        this.form.startDate,
          endDate:          this.form.endDate,
          observations:     this.form.observations || null,
        })
        Alert.success('Solicitud creada correctamente')
        this.$router.push({ name: 'vacation-requests-list' })
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al crear la solicitud')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
  },
  async created() {
    try {
      const [companiesResp, configResp] = await Promise.all([
        CompanyService.listAll(),
        VacationService.getAdvanceDaysConfig().catch(() => ({ payload: { minAdvanceDays: 3 } })),
      ])
      this.companies = (companiesResp.payload || []).map(c => ({ key: c.id, label: c.name }))
      this.minAdvanceDays = (configResp.payload && configResp.payload.minAdvanceDays) || 3
      this.minStartDate = moment().subtract(this.minAdvanceDays, 'days').format('YYYY-MM-DD')
    } catch (e) {
      console.error(e)
    }
    // Prefill opcional desde query (?companyId=&employeeId=&vacationPeriodId=)
    if (this.$route.query.vacationPeriodId) {
      this.preselectedPeriodId = Number(this.$route.query.vacationPeriodId)
    }
    if (this.$route.query.companyId) {
      await this.onCompanyChange(Number(this.$route.query.companyId))
      if (this.$route.query.employeeId) {
        this.onEmployeeChange(Number(this.$route.query.employeeId))
      }
    }
  },
}
</script>

<style lang="stylus" scoped>
.wz-tr--selected
  background-color: var(--admin-primary-light, rgba(99, 102, 241, 0.06))
</style>
